import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import dayjs from 'dayjs';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import strings from '../../../common/Translation/Translate';
import { reportChartPaperSx } from './reportChartLayout';
import PageLoader from '../../../components/PageLoader';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

type Dimension = 'VENUE' | 'SEGMENT' | 'SOURCE' | 'MANAGER';
type ColumnGranularity = 'MONTH' | 'YEAR';
type DisplayMode = 'AMOUNT' | 'ACHIEVEMENT' | 'TREND';

interface TopPerformer {
  name: string;
  revenue: number;
}

const RANKING_DIMENSIONS = ['VENUE', 'SOURCE', 'MANAGER'] as const;
type RankingDimension = (typeof RANKING_DIMENSIONS)[number];

const TOP_PERFORMER_LABELS: Record<RankingDimension, string> = {
  VENUE: strings.reports_top_venue,
  SOURCE: strings.reports_top_source,
  MANAGER: strings.reports_top_manager,
};

const displayToggleSx = {
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  border: '1px solid rgba(148, 163, 184, 0.35)',
  p: '3px',
  '& .MuiToggleButtonGroup-grouped': {
    border: 'none !important',
    borderRadius: '6px !important',
    m: 0,
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.76rem',
    whiteSpace: 'nowrap',
    px: 1.5,
    py: 0.5,
    height: 32,
    lineHeight: 1,
    border: 'none',
    borderRadius: '6px',
    color: '#64748b',
    transition: 'all 0.15s ease',
    '&.Mui-selected': {
      backgroundColor: '#1565C0',
      color: '#fff',
      fontWeight: 600,
      boxShadow: '0 8px 18px rgba(21, 101, 192, 0.24)',
      '&:hover': {
        backgroundColor: '#0D47A1',
      },
    },
    '&:hover': {
      backgroundColor: '#e2e8f0',
    },
  },
};

const premiumCardSx = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  border: '1px solid rgba(148, 163, 184, 0.28)',
  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
};

interface PeriodDetail {
  monthKey: string;
  monthLabel: string;
  budget: number;
  revenue: number;
  achievementPct: number | null;
  gap: number;
  status: string;
}

interface PerformanceRow {
  referenceId: number | null;
  referenceName: string;
  budget: number;
  revenue: number;
  gap: number;
  columnDetails: PeriodDetail[];
  monthlyDetails: PeriodDetail[];
}

interface PerformanceResponse {
  dimension: Dimension;
  columnGranularity: ColumnGranularity;
  columnKeys: string[];
  columnLabels: string[];
  kpis: {
    annualBudget: number;
    revenue: number;
    gap: number;
  };
  rows: PerformanceRow[];
}

interface EventBreakupItem {
  eventType: string;
  bookingCount: number;
  confirmedCount: number;
  tentativeCount: number;
  confirmedRevenue: number;
  tentativeRevenue: number;
  totalRevenue: number;
}

interface CellDetailResponse {
  referenceName: string;
  monthKey: string;
  monthLabel: string;
  budget: number;
  revenue: number;
  achievementPct: number | null;
  gap: number;
  bookingCount: number;
  confirmedCount: number;
  tentativeCount: number;
  confirmedRevenue: number;
  tentativeRevenue: number;
  eventBreakup: EventBreakupItem[];
}

interface SelectedCell {
  referenceId: number | null;
  referenceName: string;
  columnKey: string;
  columnLabel: string;
}

const DIMENSIONS: { key: Dimension; label: string }[] = [
  { key: 'VENUE', label: strings.reports_dimension_venue },
  { key: 'SEGMENT', label: strings.reports_dimension_segment },
  { key: 'SOURCE', label: strings.reports_dimension_source },
  { key: 'MANAGER', label: strings.reports_dimension_manager },
];

const STICKY_FIRST_WIDTH = 160;
const STICKY_TOTAL_WIDTH = 112;

const stickyFirstCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderRight: '1px solid',
  borderColor: 'divider',
  minWidth: STICKY_FIRST_WIDTH,
  maxWidth: 200,
  boxShadow: '4px 0 8px -2px rgba(15, 23, 42, 0.08)',
};

const stickyTotalCellSx = {
  position: 'sticky',
  right: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderLeft: '1px solid',
  borderColor: 'divider',
  minWidth: STICKY_TOTAL_WIDTH,
  boxShadow: '-4px 0 8px -2px rgba(15, 23, 42, 0.08)',
};

const stickyHeaderFirstCellSx = {
  ...stickyFirstCellSx,
  zIndex: 4,
  bgcolor: 'grey.50',
};

const stickyHeaderTotalCellSx = {
  ...stickyTotalCellSx,
  zIndex: 4,
  bgcolor: 'grey.50',
};

function cellColors(status: string): { bg: string; color: string } {
  switch (status) {
    case 'GREEN':
      return { bg: 'rgba(76, 175, 80, 0.22)', color: '#1B5E20' };
    case 'AMBER':
      return { bg: 'rgba(255, 193, 7, 0.28)', color: '#E65100' };
    case 'RED':
      return { bg: 'rgba(244, 67, 54, 0.2)', color: '#B71C1C' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
}

function formatAmount(value: number): string {
  return formatCurrencyWithSymbol(value);
}

function isBudgetConfigured(budget: number): boolean {
  return budget > 0;
}

function formatBudgetDisplay(budget: number): string {
  if (!isBudgetConfigured(budget)) {
    return strings.reports_budget_not_configured;
  }
  return formatAmount(budget);
}

function formatGap(value: number): string {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatCurrencyWithSymbol(value)}`;
}

function formatAchievementPct(revenue: number, budget: number): { text: string; color: string } {
  if (!isBudgetConfigured(budget)) {
    return { text: '—', color: 'text.secondary' };
  }
  const pct = (revenue / budget) * 100;
  return {
    text: `${pct.toFixed(1)}%`,
    color: pct >= 100 ? 'success.main' : pct >= 80 ? 'warning.main' : 'error.main',
  };
}

function getDimensionKpiLabels(dimension: Dimension) {
  switch (dimension) {
    case 'VENUE':
      return {
        budget: strings.reports_kpi_annual_budget,
        revenue: strings.reports_kpi_revenue,
        third: strings.reports_kpi_achievement,
        gap: strings.reports_kpi_gap,
      };
    case 'MANAGER':
      return {
        budget: 'Budget',
        revenue: strings.reports_kpi_revenue,
        third: strings.reports_kpi_achievement,
        gap: strings.reports_kpi_gap,
      };
    case 'SOURCE':
      return {
        budget: strings.budget_text_target,
        revenue: strings.reports_kpi_revenue,
        third: strings.reports_kpi_conversion,
        gap: strings.reports_kpi_gap,
      };
    case 'SEGMENT':
    default:
      return {
        budget: strings.budget_text_target,
        revenue: strings.reports_kpi_revenue,
        third: strings.reports_kpi_achievement,
        gap: strings.reports_kpi_gap,
      };
  }
}

function formatThirdKpiValue(
  dimension: Dimension,
  revenue: number,
  budget: number,
  gap: number,
): { text: string; color: string } {
  if (dimension === 'SOURCE') {
    return formatAchievementPct(revenue, budget);
  }
  return formatAchievementVsTarget(gap, budget);
}

function formatAchievementVsTarget(gap: number, budget: number): { text: string; color: string } {
  if (!isBudgetConfigured(budget)) {
    return { text: '—', color: 'text.secondary' };
  }
  if (gap === 0) {
    return { text: strings.reports_on_target, color: 'success.main' };
  }
  if (gap > 0) {
    return {
      text: `${formatGap(gap)} ${strings.reports_above_target}`,
      color: 'success.main',
    };
  }
  return {
    text: `${formatGap(gap)} ${strings.reports_below_target}`,
    color: 'error.main',
  };
}

function gapColor(gap: number): string {
  if (gap > 0) return 'success.main';
  if (gap < 0) return 'error.main';
  return 'text.secondary';
}

function formatMonthLabel(monthKey: string): string {
  return dayjs(`${monthKey}-01`).format('MMM-YYYY');
}

function cellDisplayValue(detail: PeriodDetail, mode: DisplayMode): string {
  if (mode === 'ACHIEVEMENT') {
    if (detail.achievementPct != null) {
      return `${detail.achievementPct.toFixed(1)}%`;
    }
    if (isBudgetConfigured(detail.budget)) {
      return `${((detail.revenue / detail.budget) * 100).toFixed(1)}%`;
    }
    return '—';
  }
  return formatAmount(detail.revenue);
}

function resolveTopPerformer(rows: PerformanceRow[]): TopPerformer | null {
  const top = [...rows].sort((a, b) => b.revenue - a.revenue).find(row => row.revenue > 0);
  if (!top) {
    return null;
  }
  return { name: top.referenceName, revenue: top.revenue };
}

function buildTrendColumns(data: PerformanceResponse): Array<{ key: string; label: string; revenue: number; budget: number }> {
  return data.columnKeys.map((key, index) => {
    const rows = data.rows ?? [];
    return {
      key,
      label: data.columnLabels[index] ?? key,
      revenue: rows.reduce((sum, row) => sum + (row.columnDetails[index]?.revenue ?? 0), 0),
      budget: rows.reduce((sum, row) => sum + (row.columnDetails[index]?.budget ?? 0), 0),
    };
  });
}

function buildRowTotalDetail(row: PerformanceRow): PeriodDetail {
  const { budget, revenue, gap } = row;
  let status = 'GREY';
  if (isBudgetConfigured(budget)) {
    const pct = (revenue / budget) * 100;
    if (pct >= 100) status = 'GREEN';
    else if (pct >= 80) status = 'AMBER';
    else status = 'RED';
  }
  return {
    monthKey: 'total',
    monthLabel: strings.totalText,
    budget,
    revenue,
    achievementPct: isBudgetConfigured(budget) ? (revenue / budget) * 100 : null,
    gap,
    status,
  };
}

const heatmapCellSx = {
  fontWeight: 700,
  fontSize: '0.78rem',
  p: 1,
  minWidth: 96,
  cursor: 'pointer',
  borderBottom: '1px solid',
  borderColor: 'divider',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease, background-color 0.18s ease',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.03)',
    boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.5), 0 8px 18px rgba(15, 23, 42, 0.14)',
    filter: 'brightness(1.07) saturate(1.1)',
    zIndex: 1,
  },
  '&:active': {
    transform: 'translateY(0) scale(0.99)',
    boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.75), 0 2px 6px rgba(15, 23, 42, 0.1)',
    filter: 'brightness(1.02)',
  },
};

function HeatmapCell({
  detail,
  columnLabel,
  referenceName,
  displayMode,
  onClick,
  stickySx,
  disableClick = false,
}: {
  detail: PeriodDetail;
  columnLabel: string;
  referenceName: string;
  displayMode: DisplayMode;
  onClick?: () => void;
  stickySx?: Record<string, unknown>;
  disableClick?: boolean;
}) {
  const colors = cellColors(detail.status);
  const achievement = formatAchievementVsTarget(detail.gap, detail.budget);
  const clickable = !disableClick && !!onClick;
  const achievementPctDisplay =
    detail.achievementPct != null
      ? `${detail.achievementPct.toFixed(1)}%`
      : isBudgetConfigured(detail.budget)
        ? `${((detail.revenue / detail.budget) * 100).toFixed(1)}%`
        : '—';

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={250}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'grey.900',
            color: 'common.white',
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            maxWidth: 280,
          },
        },
      }}
      title={
        <Box sx={{ fontSize: '0.75rem', lineHeight: 1.65, py: 0.25 }}>
          <Box sx={{ fontWeight: 700, mb: 0.5 }}>
            {referenceName} · {columnLabel}
          </Box>
          <Box>
            {strings.budget_text_target}: {formatBudgetDisplay(detail.budget)}
          </Box>
          <Box>
            {strings.reports_kpi_revenue}: {formatAmount(detail.revenue)}
          </Box>
          <Box>
            {strings.reports_kpi_achievement}: {achievementPctDisplay}
          </Box>
          <Box sx={{ color: detail.gap > 0 ? '#A5D6A7' : detail.gap < 0 ? '#EF9A9A' : 'grey.400' }}>
            {strings.reports_kpi_gap}: {achievement.text}
          </Box>
        </Box>
      }>
      <TableCell
        align="center"
        onClick={clickable ? onClick : undefined}
        sx={{
          ...heatmapCellSx,
          ...stickySx,
          bgcolor: colors.bg,
          color: colors.color,
          position: stickySx ? 'sticky' : 'relative',
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': clickable
            ? heatmapCellSx['&:hover']
            : stickySx
              ? { bgcolor: colors.bg }
              : undefined,
        }}>
        {cellDisplayValue(detail, displayMode)}
      </TableCell>
    </Tooltip>
  );
}

function PerformanceTrendChart({ data }: { data: PerformanceResponse }) {
  const columns = buildTrendColumns(data);
  const maxValue = Math.max(...columns.flatMap(column => [column.revenue, column.budget]), 1);

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 270,
        borderRadius: '14px',
        border: '1px solid rgba(148, 163, 184, 0.28)',
        bgcolor: '#FFFFFF',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0F172A' }}>
            Revenue Trend
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            Revenue against configured target
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#1565C0' }} />
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
            Revenue
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: '#93C5FD' }} />
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
            Target
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 190,
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(42px, 1fr))`,
          alignItems: 'end',
          gap: 1,
          borderBottom: '1px solid rgba(148, 163, 184, 0.3)',
          borderLeft: '1px solid rgba(148, 163, 184, 0.18)',
          px: 1,
          pt: 1,
        }}>
        {columns.map(column => (
          <Tooltip
            key={column.key}
            arrow
            title={`${column.label}\nRevenue: ${formatAmount(column.revenue)}\nTarget: ${formatBudgetDisplay(column.budget)}`}>
            <Box sx={{ minWidth: 0, height: '100%', display: 'flex', alignItems: 'end', gap: 0.35, justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 12,
                  height: `${Math.max((column.revenue / maxValue) * 100, column.revenue > 0 ? 5 : 0)}%`,
                  borderRadius: '8px 8px 0 0',
                  bgcolor: '#1565C0',
                  boxShadow: '0 8px 16px rgba(21, 101, 192, 0.22)',
                }}
              />
              <Box
                sx={{
                  width: 12,
                  height: `${Math.max((column.budget / maxValue) * 100, column.budget > 0 ? 5 : 0)}%`,
                  borderRadius: '8px 8px 0 0',
                  bgcolor: '#93C5FD',
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(42px, 1fr))`,
          gap: 1,
          px: 1,
          mt: 1,
        }}>
        {columns.map(column => (
          <Typography key={column.key} variant="caption" sx={{ color: '#64748B', textAlign: 'center', fontWeight: 700 }}>
            {column.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

interface Props {
  startDate: string;
  endDate: string;
}

const DimensionPerformanceByMonth: FC<Props> = ({ startDate, endDate }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [dimension, setDimension] = useState<Dimension>('VENUE');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('AMOUNT');
  const [loading, setLoading] = useState(false);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [data, setData] = useState<PerformanceResponse | null>(null);
  const [topPerformers, setTopPerformers] = useState<Partial<Record<RankingDimension, TopPerformer>>>({});
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [yearDrill, setYearDrill] = useState<{
    referenceId: number | null;
    referenceName: string;
    year: string;
    months: PeriodDetail[];
  } | null>(null);
  const [cellDetail, setCellDetail] = useState<CellDetailResponse | null>(null);
  const [cellDetailLoading, setCellDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isTrendMode = displayMode === 'TREND';
  const isYearView = data?.columnGranularity === 'YEAR';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: response } = await axiosInstance.post(URLS.dimensionPerformanceByMonth, {
        startDate: dayjs(startDate).format('DD-MM-YYYY'),
        endDate: dayjs(endDate).format('DD-MM-YYYY'),
        dimension,
      });
      setData(response);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dimension]);

  const fetchTopPerformers = useCallback(async () => {
    try {
      setRankingsLoading(true);
      const payload = {
        startDate: dayjs(startDate).format('DD-MM-YYYY'),
        endDate: dayjs(endDate).format('DD-MM-YYYY'),
      };
      const results = await Promise.all(
        RANKING_DIMENSIONS.map(rankingDimension =>
          axiosInstance.post(URLS.dimensionPerformanceByMonth, {
            ...payload,
            dimension: rankingDimension,
          }),
        ),
      );
      const next: Partial<Record<RankingDimension, TopPerformer>> = {};
      RANKING_DIMENSIONS.forEach((rankingDimension, index) => {
        const top = resolveTopPerformer(results[index]?.data?.rows ?? []);
        if (top) {
          next[rankingDimension] = top;
        }
      });
      setTopPerformers(next);
    } catch {
      setTopPerformers({});
    } finally {
      setRankingsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
    setSelectedCell(null);
    setYearDrill(null);
    setDrawerOpen(false);
  }, [fetchData]);

  useEffect(() => {
    fetchTopPerformers();
  }, [fetchTopPerformers]);

  const fetchCellDetail = useCallback(
    async (row: PerformanceRow, monthKey: string) => {
      try {
        setCellDetailLoading(true);
        const { data: response } = await axiosInstance.post(URLS.dimensionPerformanceCellDetail, {
          startDate: dayjs(startDate).format('DD-MM-YYYY'),
          endDate: dayjs(endDate).format('DD-MM-YYYY'),
          dimension,
          referenceId: row.referenceId,
          referenceName: row.referenceName,
          monthKey,
        });
        setCellDetail(response);
      } catch {
        setCellDetail(null);
      } finally {
        setCellDetailLoading(false);
      }
    },
    [startDate, endDate, dimension],
  );

  const openMonthDrawer = useCallback(
    (row: PerformanceRow, monthKey: string, monthLabel: string) => {
      setSelectedCell({
        referenceId: row.referenceId,
        referenceName: row.referenceName,
        columnKey: monthKey,
        columnLabel: monthLabel,
      });
      setDrawerOpen(true);
      fetchCellDetail(row, monthKey);
    },
    [fetchCellDetail],
  );

  const handleCellClick = (row: PerformanceRow, detail: PeriodDetail, columnLabel: string) => {
    if (isYearView) {
      const months = row.monthlyDetails.filter(m => m.monthKey.startsWith(`${detail.monthKey}-`));
      setYearDrill({
        referenceId: row.referenceId,
        referenceName: row.referenceName,
        year: detail.monthKey,
        months,
      });
      return;
    }
    openMonthDrawer(row, detail.monthKey, columnLabel);
  };

  const columnLabel =
    dimension === 'VENUE'
      ? strings.budget_column_venue
      : dimension === 'SEGMENT'
        ? 'Event Segment'
        : dimension === 'SOURCE'
          ? strings.source_title_list
          : strings.budget_column_manager;

  const periodLabel = `${dayjs(startDate).format('MMM YYYY')} – ${dayjs(endDate).format('MMM YYYY')}`;
  const kpiLabels = getDimensionKpiLabels(dimension);
  const thirdKpi = data
    ? formatThirdKpiValue(dimension, data.kpis.revenue, data.kpis.annualBudget, data.kpis.gap)
    : null;

  const cellThirdKpi = useMemo(() => {
    if (!cellDetail) return null;
    return formatThirdKpiValue(dimension, cellDetail.revenue, cellDetail.budget, cellDetail.gap);
  }, [cellDetail, dimension]);

  const drawerTitle = useMemo(() => {
    if (!selectedCell) return strings.reports_cell_detail_title;
    return `${selectedCell.referenceName} · ${selectedCell.columnLabel}`;
  }, [selectedCell]);

  return (
    <Paper sx={{ ...reportChartPaperSx, ...premiumCardSx }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>
          {strings.reports_performance_by_month_title}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.4 }}>
          Compare revenue performance by venue, segment, source and manager.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          const nextKey = DIMENSIONS[v]?.key;
          if (nextKey) {
            setDimension(nextKey);
          }
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        {DIMENSIONS.map((d, i) => (
          <Tab key={d.key} label={d.label} value={i} />
        ))}
      </Tabs>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {RANKING_DIMENSIONS.map(rankingDimension => {
          const performer = topPerformers[rankingDimension];
          const tabIndex = DIMENSIONS.findIndex(d => d.key === rankingDimension);
          const Icon =
            rankingDimension === 'VENUE'
              ? EmojiEventsRoundedIcon
              : rankingDimension === 'SOURCE'
                ? SourceOutlinedIcon
                : GroupsRoundedIcon;
          return (
            <Grid item xs={12} sm={4} key={rankingDimension}>
              <Box
                onClick={() => {
                  if (tabIndex >= 0) {
                    setActiveTab(tabIndex);
                    setDimension(rankingDimension);
                  }
                }}
                sx={{
                  cursor: tabIndex >= 0 ? 'pointer' : 'default',
                  height: '100%',
                  borderRadius: '14px',
                  px: 1.5,
                  py: 1.25,
                  bgcolor: '#F8FAFC',
                  border: '1px solid rgba(148, 163, 184, 0.24)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
                  '&:hover': tabIndex >= 0
                    ? {
                        transform: 'translateY(-1px)',
                        borderColor: '#1565C0',
                        boxShadow: '0 12px 26px rgba(21, 101, 192, 0.13)',
                      }
                    : undefined,
                }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '12px',
                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                    color: '#1565C0',
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                  }}>
                  <Icon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }}>
                    {TOP_PERFORMER_LABELS[rankingDimension]}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.25, lineHeight: 1.25, color: '#0F172A' }}>
                    {rankingsLoading ? '…' : performer?.name ?? '—'}
                  </Typography>
                  {!rankingsLoading && performer && (
                    <Typography variant="caption" sx={{ color: '#1565C0', fontWeight: 800 }}>
                      {formatAmount(performer.revenue)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {loading && <PageLoader />}

      {!loading && data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              {
                label: kpiLabels.revenue,
                value: formatAmount(data.kpis.revenue),
                icon: <TrendingUpRoundedIcon />,
                bg: 'rgba(76, 175, 80, 0.12)',
                iconColor: '#2E7D32',
              },
              {
                label: kpiLabels.budget,
                value: formatBudgetDisplay(data.kpis.annualBudget),
                icon: <AccountBalanceWalletOutlinedIcon />,
                bg: 'rgba(25, 118, 210, 0.1)',
                iconColor: '#1565C0',
              },
              {
                label: kpiLabels.third,
                value: thirdKpi?.text ?? '—',
                color: thirdKpi?.color,
                icon: <QueryStatsOutlinedIcon />,
                bg: 'rgba(124, 58, 237, 0.1)',
                iconColor: '#6D28D9',
              },
              {
                label: kpiLabels.gap,
                value: formatGap(data.kpis.gap),
                color: gapColor(data.kpis.gap),
                icon: data.kpis.gap >= 0 ? <TrendingUpRoundedIcon /> : <TrendingDownRoundedIcon />,
                bg: data.kpis.gap >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.1)',
                iconColor: data.kpis.gap >= 0 ? '#2E7D32' : '#C62828',
              },
            ].map(kpi => (
              <Grid item xs={12} sm={6} md={3} key={kpi.label}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: '14px',
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    bgcolor: '#FFFFFF',
                  }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '12px',
                      bgcolor: kpi.bg,
                      color: kpi.iconColor,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: 20 },
                    }}>
                    {kpi.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800 }} display="block">
                      {kpi.label}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        color: kpi.color || '#0F172A',
                        mt: 0.25,
                        fontSize: kpi.label === kpiLabels.third ? '0.95rem' : undefined,
                        lineHeight: 1.25,
                      }}>
                      {kpi.value}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {strings.reports_selected_period}: {periodLabel}
              </Typography>
              {!isTrendMode && (
                <Typography variant="caption" color="text.secondary">
                  {isYearView ? strings.reports_year_column_hint : strings.reports_click_cell_hint}
                </Typography>
              )}
            </Box>
            <ToggleButtonGroup
              value={displayMode}
              exclusive
              size="small"
              onChange={(_, value: DisplayMode | null) => {
                if (value) setDisplayMode(value);
              }}
              sx={displayToggleSx}>
              <ToggleButton value="AMOUNT">{strings.reports_display_amount}</ToggleButton>
              <ToggleButton value="ACHIEVEMENT">{strings.reports_display_achievement_pct}</ToggleButton>
              <ToggleButton value="TREND">Revenue Trend</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {!isTrendMode ? (
            <TableContainer
              sx={{
                maxHeight: 520,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'rgba(148, 163, 184, 0.28)',
                borderRadius: '14px',
                width: '100%',
              }}>
              <Table stickyHeader size="small" sx={{ minWidth: STICKY_FIRST_WIDTH + STICKY_TOTAL_WIDTH + data.columnKeys.length * 96 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyHeaderFirstCellSx, fontWeight: 800 }}>{columnLabel}</TableCell>
                    {data.columnLabels.map((label, idx) => (
                      <TableCell
                        key={data.columnKeys[idx]}
                        align="center"
                        sx={{
                          fontWeight: 800,
                          minWidth: 96,
                          whiteSpace: 'nowrap',
                          bgcolor: '#F8FAFC',
                        }}>
                        {label}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ ...stickyHeaderTotalCellSx, fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {strings.totalText}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={data.columnKeys.length + 2} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {strings.reports_no_performance_data}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.rows.map(row => {
                      const totalDetail = buildRowTotalDetail(row);
                      return (
                      <TableRow
                        key={`${row.referenceId ?? 'n'}-${row.referenceName}`}
                        sx={{
                          '&:hover td:first-of-type': { bgcolor: 'action.hover' },
                          '&:hover td:last-of-type': { bgcolor: cellColors(totalDetail.status).bg },
                        }}>
                        <TableCell sx={{ ...stickyFirstCellSx, fontWeight: 700 }}>{row.referenceName}</TableCell>
                        {row.columnDetails.map((detail, idx) => (
                          <HeatmapCell
                            key={`${row.referenceName}-${detail.monthKey}`}
                            detail={detail}
                            columnLabel={data.columnLabels[idx] ?? detail.monthLabel}
                            referenceName={row.referenceName}
                            displayMode={displayMode}
                            onClick={() => handleCellClick(row, detail, data.columnLabels[idx] ?? detail.monthLabel)}
                          />
                        ))}
                        <HeatmapCell
                          detail={totalDetail}
                          columnLabel={strings.totalText}
                          referenceName={row.referenceName}
                          displayMode={displayMode}
                          stickySx={stickyTotalCellSx}
                          disableClick
                        />
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ width: '100%' }}>
              <PerformanceTrendChart data={data} />
            </Box>
          )}
        </>
      )}

      <Dialog open={!!yearDrill} onClose={() => setYearDrill(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>
            {yearDrill?.referenceName} — {yearDrill?.year}
          </span>
          <IconButton onClick={() => setYearDrill(null)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(100px, 1fr) minmax(88px, 1fr) minmax(88px, 1fr)',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
              {strings.budget_text_month}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', textAlign: 'right' }}>
              {strings.budget_text_target}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', textAlign: 'right' }}>
              {strings.reports_kpi_revenue}
            </Typography>
          </Box>
          <List disablePadding>
            {(yearDrill?.months ?? []).map(month => (
              <ListItemButton
                key={month.monthKey}
                onClick={() => {
                  const row = data?.rows.find(
                    r => r.referenceName === yearDrill?.referenceName && r.referenceId === yearDrill?.referenceId,
                  );
                  if (!row) return;
                  setYearDrill(null);
                  openMonthDrawer(row, month.monthKey, formatMonthLabel(month.monthKey));
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(100px, 1fr) minmax(88px, 1fr) minmax(88px, 1fr)',
                  gap: 1,
                  alignItems: 'center',
                  py: 1.25,
                }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatMonthLabel(month.monthKey)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'right',
                    color: isBudgetConfigured(month.budget) ? 'text.primary' : 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {formatBudgetDisplay(month.budget)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    textAlign: 'right',
                    color: cellColors(month.status).color,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {formatAmount(month.revenue)}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', pr: 1 }}>
            {drawerTitle}
          </Typography>
          <IconButton onClick={() => setDrawerOpen(false)} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          {cellDetailLoading && <PageLoader />}
          {!cellDetailLoading && cellDetail && (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  { label: kpiLabels.budget, value: formatBudgetDisplay(cellDetail.budget) },
                  { label: kpiLabels.revenue, value: formatAmount(cellDetail.revenue) },
                  {
                    label: kpiLabels.third,
                    value: cellThirdKpi?.text ?? '—',
                    color: cellThirdKpi?.color,
                  },
                  { label: kpiLabels.gap, value: formatGap(cellDetail.gap), color: gapColor(cellDetail.gap) },
                  { label: strings.reports_booking_count, value: String(cellDetail.bookingCount) },
                ].map(item => (
                  <Grid item xs={6} key={item.label}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: item.color || 'text.primary', mt: 0.25 }}>
                      {item.value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {strings.reports_confirmed_tentative_split}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  size="small"
                  label={`${strings.reports_text_confirmed_bookings}: ${cellDetail.confirmedCount} · ${formatAmount(cellDetail.confirmedRevenue)}`}
                  sx={{ bgcolor: 'rgba(76, 175, 80, 0.12)', color: 'success.dark', fontWeight: 600 }}
                />
                <Chip
                  size="small"
                  label={`${strings.reports_text_tentative_bookings}: ${cellDetail.tentativeCount} · ${formatAmount(cellDetail.tentativeRevenue)}`}
                  sx={{ bgcolor: 'rgba(33, 150, 243, 0.12)', color: 'primary.main', fontWeight: 600 }}
                />
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {strings.reports_event_breakup}
              </Typography>
              {cellDetail.eventBreakup.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {strings.reports_no_performance_data}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{strings.budget_column_event_type}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {strings.reports_booking_count}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        {strings.reports_kpi_revenue}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cellDetail.eventBreakup.map(event => (
                      <TableRow key={event.eventType}>
                        <TableCell>{event.eventType}</TableCell>
                        <TableCell align="right">{event.bookingCount}</TableCell>
                        <TableCell align="right">{formatAmount(event.totalRevenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </Paper>
  );
};

export default DimensionPerformanceByMonth;
